import { useLayoutEffect } from "react";
import { RegisterOptions, useWatch } from "react-hook-form";
import DatePicker from "components/Form/DatePicker";
import { FLUSHED_BORDER_BASE, FLUSHED_ACTIVE_BOTTOM_BORDER } from "definitions/constants/styling";
import { useFloatingField } from "./context";
import { sizeStyles } from "./sizeStyles";

type FloatingCalendarProps = {
  control: any;
  name: string;
  rules?: RegisterOptions;
  defaultValue?: any;
  onDateChange?: (date: Date) => void;
  onBlur?: () => void;
  disabled?: boolean;
  minDate?: Date;
  isClearable?: boolean;
  dateFormat?: string;
};

function FloatingCalendar({
  control,
  name,
  rules,
  defaultValue,
  onDateChange,
  onBlur,
  ...rest
}: FloatingCalendarProps) {
  const { setHasValue, size } = useFloatingField();
  const styles = sizeStyles[size];
  const value = useWatch({ control, name });

  useLayoutEffect(() => {
    setHasValue(!!value);
  }, [value, setHasValue]);

  // Do NOT wrap onDateChange — ControlledDatePicker skips its internal onChange when
  // onDateChange is provided, which would break form state. We track hasValue via
  // useWatch instead and pass the user's callback through unchanged.
  return (
    <DatePicker
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue}
      onDateChange={onDateChange}
      triggerButtonProps={{
        height: styles.inputHeight,
        paddingTop: styles.calendarPaddingTop,
        fontSize: styles.fontSize,
        variant: "ghost",
        className: "floating-button",
        onBlur,
        sx: {
          display: "inline-flex",
          ...FLUSHED_BORDER_BASE,
          bg: "transparent",
          _hover: { bg: "transparent" },
          _active: FLUSHED_ACTIVE_BOTTOM_BORDER,
          _focusVisible: { ...FLUSHED_ACTIVE_BOTTOM_BORDER, boxShadow: "none" },
          "&[aria-expanded='true']": FLUSHED_ACTIVE_BOTTOM_BORDER,
        },
      }}
      {...rest}
    />
  );
}

export default FloatingCalendar;
